import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useHubUser } from "@/hooks/user/useHubUser";
import { leaveAutomaticStreakQueueV2 } from "@/utils/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

export interface UseLeaveStreakQueueOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Flips the user's queue item to OUT for a given campaign. Idempotent on the
 * UX side: callers handle the "already left" case by relying on the queue-state
 * query, which collapses OUT to `null` (same shape as "never opted in").
 */
export const useLeaveStreakQueue = (options?: UseLeaveStreakQueueOptions) => {
  const { auth } = useHubUser();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: async (campaignName: string) => {
      const result = await leaveAutomaticStreakQueueV2(auth, campaignName);

      if (isServerApiRfcError(result)) {
        throw new Error(result.detail);
      }
      if (isServerApiError(result)) {
        throw new Error(result.message);
      }

      return result;
    },
    onSuccess: (_data, _campaignName) => {
      // Read hook key shape: [AUTOMATIC_STREAK, "queue", "item", defiAddress, campaignName].
      // Invalidate by the "item" prefix so React Query's prefix match actually
      // hits the subscribed query — passing `campaignName` at index 2 silently
      // matches nothing and leaves the UI on the stale queue row.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, "queue", "item"] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAKS] });
      // Leaving refunds unspent priority tickets — refresh the balance.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, "priority-tickets"] });

      enqueueSnackbar("You've left the autopilot queue.", { variant: "info" });

      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to leave queue: ${error.message}`, { variant: "error" });

      options?.onError?.(error.message);
    },
  });

  return {
    leaveStreakQueue: mutation.mutateAsync,
    isLeaving: mutation.isPending,
    error: mutation.error?.message,
  };
};
